import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@prio365/prio365-react-library';
import { useTranslation } from 'react-i18next';

import {
  getContactRedirect,
  getContactsDrawerState,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import ContactDetails from './ContactDetails';
import { makePrioStyles } from '../../../theme/utils';
import {
  setContactsDrawerView,
  setContactsDrawerState,
} from '../../contacts/actions/drawer';
import { InternalContact, ExternalContact } from '../../../models/Contact';
import PrioSpinner from '../../../components/PrioSpinner';
import { ContactId } from '../../../models/Types';
import useContactsContext from '../hooks/useContactsProvider';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  noPadding: {
    padding: 0,
  },
  contactDetails: {
    height: '100%',
    backgroundColor: theme.old.palette.backgroundPalette.content,
    alignSelf: 'stretch',
    overflow: 'hidden',
  },
  backButton: {
    marginBottom: theme.old.spacing.unit(2),
  },
}));

interface ContactDetailsDrawerProps {
  contactId?: ContactId;
  isSubDrawer?: boolean;
  onEdit?: (contactId: ContactId) => void;
  onRedirect?: (contactId: ContactId) => void;
}

export const ContactDetailsDrawer: React.FC<ContactDetailsDrawerProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const {
    isSubDrawer,
    contactId: componentContactId,
    onEdit,
    onRedirect,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const currentURLPath = window.location.pathname;
  const isContactSearchPage = currentURLPath.includes('contacts/contactSearch');

  const { selectedContact } = useSelector(getContactsDrawerState);
  const contactId = useMemo(
    () =>
      isSubDrawer && componentContactId ? componentContactId : selectedContact,
    [isSubDrawer, componentContactId, selectedContact]
  );

  const { getContactById } = useContactsContext();

  const contact = useMemo(() => {
    return getContactById(contactId);
  }, [contactId, getContactById]);

  const redirect = useSelector<RootReducerState, string>((state) =>
    getContactRedirect(state, contactId)
  );
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const goBack = () => dispatch(setContactsDrawerView('list'));
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (redirect) {
      if (isSubDrawer && onRedirect) {
        onRedirect(redirect);
      } else {
        dispatch(setContactsDrawerState({ selectedContact: redirect }));
      }
    }
  }, [redirect, isSubDrawer, onRedirect, dispatch]);
  //#endregion

  if (contact) {
    return (
      <>
        {!isSubDrawer && !isContactSearchPage && (
          <Button
            onClick={goBack}
            type="link"
            iconProp={['fal', 'chevron-left']}
            className={classes.backButton}
          >
            {t('common:back')}
          </Button>
        )}
        <ContactDetails
          contactId={contactId}
          contact={contact as InternalContact & ExternalContact}
          className={classNames(classes.contactDetails, classes.noPadding)}
          contentClassName={classes.noPadding}
          onEdit={onEdit}
          isSubDrawer={isSubDrawer}
          noAvatar
        />
      </>
    );
  }

  return (
    <div className="prio-flex-center-center prio-flex-column prio-container-fullscreen-height">
      <PrioSpinner size="large" />
    </div>
  );
};

export default ContactDetailsDrawer;
